
import React from 'react';
import { IntegrationApp, Language } from '../../types';
import { TRANSLATIONS } from '../../constants';

interface IntegrationsListProps {
  language: Language;
  apps: IntegrationApp[];
  onSelectApp: (app: IntegrationApp) => void;
}

const IntegrationsList: React.FC<IntegrationsListProps> = ({ language, apps, onSelectApp }) => {
  const t = TRANSLATIONS[language];
  
  const IntegrationIcon = ({ name, icon }: { name: string, icon: string }) => {
     return <i className={`fa-brands ${icon.replace('fa-', '')} ${icon}`}></i>;
  };

  return (
      <div className="p-6 lg:p-10 h-full flex flex-col overflow-y-auto custom-scrollbar">
         <div className="mb-10">
            <h1 className="text-3xl font-black text-slate-800 dark:text-white mb-2">{t.integrationTitle}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t.integrationDesc}</p>
         </div>
         <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {apps.map(app => (
               <div 
                  key={app.id} 
                  onClick={() => onSelectApp(app)}
                  className={`
                     group relative bg-white dark:bg-[#15151a] p-6 rounded-[32px] border border-slate-200 dark:border-white/5 cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1
                     ${app.connected ? 'ring-1 ring-emerald-500/50' : ''}
                  `}
               >
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${app.color} opacity-5 rounded-bl-[100px] transition-opacity group-hover:opacity-10`}></div>
                  <div className="flex items-start justify-between mb-6">
                     <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${app.color} flex items-center justify-center text-white text-3xl shadow-lg group-hover:scale-110 transition-transform`}>
                        <IntegrationIcon name={app.name} icon={app.icon} />
                     </div>
                     {app.connected ? (
                        <div className="flex items-center gap-2 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20">
                           <span className="relative flex h-2 w-2">
                             <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                             <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                           </span>
                           <span className="text-[10px] font-black text-emerald-500 uppercase">Active</span>
                        </div>
                     ) : (
                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-300">
                           <i className="fas fa-plus"></i>
                        </div>
                     )}
                  </div>
                  <h3 className="text-xl font-black text-slate-800 dark:text-white mb-2">{app.name}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium leading-relaxed mb-4 h-8 overflow-hidden">{app.description}</p>
               </div>
            ))}
         </div>
      </div>
  );
};

export default IntegrationsList;
